import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface KPICardProps {
    icon: LucideIcon;
    label: string;
    value: string | number | ReactNode;
    trend?: {
        value: string;
        direction: 'up' | 'down' | 'neutral';
        comparison?: string;
    };
    onClick?: () => void;
    className?: string;
}

export function KPICard({ icon: Icon, label, value, trend, onClick, className }: KPICardProps) {
    const getTrendColor = () => {
        if (!trend) return '';
        switch (trend.direction) {
            case 'up':
                return 'text-emerald-400 bg-emerald-500/20';
            case 'down':
                return 'text-red-400 bg-red-500/20';
            default:
                return 'text-slate-400 bg-slate-500/20';
        }
    };

    const TrendIcon = trend?.direction === 'up' ? TrendingUp : trend?.direction === 'down' ? TrendingDown : null;

    return (
        <motion.div
            whileHover={onClick ? { y: -5 } : {}}
            className={cn(
                "bg-card border border-border rounded-xl p-4 relative overflow-hidden group transition-all",
                onClick && "cursor-pointer",
                className
            )}
            onClick={onClick}
        >
            {/* Background Icon */}
            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Icon className="w-10 h-10" />
            </div>

            {/* Icon */}
            <div className="mb-2">
                <div className="inline-flex p-1.5 bg-primary/10 rounded-lg border border-primary/20">
                    <Icon className="w-4 h-4 text-primary" />
                </div>
            </div>

            {/* Label */}
            <div className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wider mb-1.5">
                {label}
            </div>

            {/* Value */}
            <div className="text-2xl font-bold text-foreground mb-2">
                {value}
            </div>

            {/* Trend Indicator */}
            {trend && (
                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold ${getTrendColor()}`}>
                    {TrendIcon && <TrendIcon className="w-3 h-3" />}
                    <span>{trend.value}</span>
                    {trend.comparison && (
                        <span className="text-[10px] opacity-80 ml-1">{trend.comparison}</span>
                    )}
                </div>
            )}
        </motion.div>
    );
}
